import { Download, Wallet, Building2, Percent, Landmark, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { SectionCard, KpiCard, Segmented, InfoNote, Row } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput, TextInput } from "@/components/common/Inputs";
import { Sel, SwitchRow } from "@/components/common/Sel";
import { PTKP_STATUSES, JKK_OPTIONS, ptkpAmount, terCategory } from "@/lib/engines/tax";
import { fmtRp, fmtPct, MONTHS_ID, downloadCsv } from "@/lib/format";

const METHOD_OPTS = [
  { value: "gross", label: "Gross (dipotong)" },
  { value: "grossup", label: "Gross-up (tunjangan pajak)" },
  { value: "net", label: "Net (ditanggung perusahaan)" },
];

const METHOD_DESC = {
  gross: "PPh 21 dipotong dari gaji karyawan. Take home pay berkurang sebesar pajak.",
  grossup: "Perusahaan memberi tunjangan pajak sebesar PPh 21; tunjangan ikut menambah bruto. Take home tetap utuh, biaya perusahaan naik.",
  net: "Pajak ditanggung perusahaan dan tidak menambah penghasilan karyawan. Biaya pajak tidak dapat dibiayakan secara fiskal.",
};

const monthOptions = MONTHS_ID.map((m, i) => ({ value: i + 1, label: m }));
const statusOptions = PTKP_STATUSES.map((s) => ({ value: s, label: `${s} · PTKP ${fmtRp(ptkpAmount(s))} · TER ${terCategory(s)}` }));

export default function Pph21Panel({ data, setData, result, readOnly }) {
  const type = data.employeeType;
  const setType = (v) => setData((d) => ({ ...d, employeeType: v }));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">Jenis penerima</span>
        <Segmented
          value={type}
          onChange={setType}
          disabled={readOnly}
          testid="pph21-employee-type"
          options={[
            { value: "tetap", label: "Pegawai Tetap" },
            { value: "tidakTetap", label: "Pegawai Tidak Tetap / Harian" },
            { value: "bukanPegawai", label: "Bukan Pegawai" },
          ]}
        />
      </div>
      {type === "tetap" && <Tetap data={data.tetap} set={(fn) => setData((d) => ({ ...d, tetap: fn(d.tetap) }))} r={result} ro={readOnly} />}
      {type === "tidakTetap" && (
        <TidakTetap data={data.tidakTetap} set={(fn) => setData((d) => ({ ...d, tidakTetap: fn(d.tidakTetap) }))} r={result} ro={readOnly} />
      )}
      {type === "bukanPegawai" && (
        <BukanPegawai data={data.bukanPegawai} set={(fn) => setData((d) => ({ ...d, bukanPegawai: fn(d.bukanPegawai) }))} r={result} ro={readOnly} />
      )}
      <ReferenceCard />
    </div>
  );
}

function Tetap({ data, set, r, ro }) {
  const f = (k) => (v) => set((d) => ({ ...d, [k]: v }));
  const fb = (k) => (v) => set((d) => ({ ...d, bpjs: { ...d.bpjs, [k]: v } }));
  const exportCsv = () => {
    const rows = [["Bulan", "Gaji & Tunjangan", "Bonus/THR", "BPJS ditanggung perusahaan", "Tunjangan PPh", "Bruto", "Tarif TER", "PPh 21", "Potongan BPJS karyawan", "Take Home Pay", "Biaya Perusahaan"]];
    r.months.forEach((m) =>
      rows.push([MONTHS_ID[m.month - 1], m.gp + m.tt + m.tl, m.irregular, Math.round(m.jkk + m.jkm + m.kesEr), Math.round(m.tunjanganPajak), Math.round(m.bruto), m.reconciliation ? "Pasal 17" : `${m.terRate}%`, Math.round(m.pph), Math.round(m.potonganBpjs), Math.round(m.thp), Math.round(m.companyCost)])
    );
    downloadCsv("pph21-jadwal-tahunan.csv", rows);
  };
  return (
    <div className="grid xl:grid-cols-[420px_1fr] gap-5">
      <SectionCard title="Input Penghasilan" eyebrow="Pegawai Tetap" testid="pph21-input-card">
        <div className="space-y-4">
          <Field label="Metode perhitungan" hint={METHOD_DESC[data.method]}>
            <Segmented value={data.method} onChange={f("method")} options={METHOD_OPTS} testid="pph21-method" disabled={ro} size="sm" />
          </Field>
          <Field label="Nilai yang diinput">
            <Segmented
              value={data.inputBasis}
              onChange={f("inputBasis")}
              testid="pph21-input-basis"
              disabled={ro}
              size="sm"
              options={[
                { value: "bruto", label: "Gaji bruto (sebelum pajak)" },
                { value: "takehome", label: "Target take home (net)" },
              ]}
            />
          </Field>
          {data.inputBasis === "takehome" ? (
            <Field label="Target take home pay per bulan" hint="Gaji pokok akan dicari otomatis agar take home bulan reguler sama dengan target.">
              <MoneyInput value={data.targetTakeHome} onChange={f("targetTakeHome")} testid="pph21-target-takehome-input" disabled={ro} />
            </Field>
          ) : (
            <Field label="Gaji pokok per bulan">
              <MoneyInput value={data.gajiPokok} onChange={f("gajiPokok")} testid="pph21-gaji-pokok-input" disabled={ro} />
            </Field>
          )}
          {data.inputBasis === "takehome" && (
            <InfoNote testid="pph21-solved-gaji">
              Gaji pokok yang dibutuhkan: <b className="num text-primary">{fmtRp(r.solvedGajiPokok)}</b>
            </InfoNote>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tunjangan tetap" tip="Tunjangan rutin yang ikut menjadi dasar iuran BPJS (mis. tunjangan jabatan).">
              <MoneyInput value={data.tunjanganTetap} onChange={f("tunjanganTetap")} testid="pph21-tunjangan-tetap-input" disabled={ro} />
            </Field>
            <Field label="Tunjangan lain / lembur" tip="Tunjangan tidak tetap per bulan: transport, makan, lembur, dll.">
              <MoneyInput value={data.tunjanganLain} onChange={f("tunjanganLain")} testid="pph21-tunjangan-lain-input" disabled={ro} />
            </Field>
          </div>
          <div className="grid grid-cols-[1fr_110px] gap-3">
            <Field label="Bonus (sekali setahun)">
              <MoneyInput value={data.bonus} onChange={f("bonus")} testid="pph21-bonus-input" disabled={ro} />
            </Field>
            <Field label="Bulan">
              <Sel value={data.bonusMonth} onChange={(v) => f("bonusMonth")(Number(v))} options={monthOptions} testid="pph21-bonus-month" disabled={ro} />
            </Field>
            <Field label="THR">
              <MoneyInput value={data.thr} onChange={f("thr")} testid="pph21-thr-input" disabled={ro} />
            </Field>
            <Field label="Bulan">
              <Sel value={data.thrMonth} onChange={(v) => f("thrMonth")(Number(v))} options={monthOptions} testid="pph21-thr-month" disabled={ro} />
            </Field>
          </div>
          <Field label="Status PTKP">
            <Sel value={data.status} onChange={f("status")} options={statusOptions} testid="pph21-status-select" disabled={ro} />
          </Field>
          <SwitchRow label="Memiliki NPWP / NIK valid" hint="Tanpa NPWP dikenakan tarif 20% lebih tinggi." checked={data.npwp} onChange={f("npwp")} testid="pph21-npwp-switch" disabled={ro} />
          <div className="gold-rule my-2" />
          <SwitchRow label="Perhitungkan BPJS" hint="JKK, JKM & BPJS Kesehatan perusahaan menambah bruto; JHT 2% & JP 1% karyawan mengurangi neto." checked={data.bpjs.enabled} onChange={fb("enabled")} testid="pph21-bpjs-switch" disabled={ro} />
          {data.bpjs.enabled && (
            <div className="space-y-3 pl-1">
              <Field label="Tarif JKK (sesuai risiko kerja)">
                <Sel value={data.bpjs.jkkRate} onChange={(v) => fb("jkkRate")(Number(v))} options={JKK_OPTIONS} testid="pph21-jkk-select" disabled={ro} />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <SwitchRow label="BPJS Kes" checked={data.bpjs.kes} onChange={fb("kes")} testid="pph21-bpjs-kes-switch" disabled={ro} />
                <SwitchRow label="JHT" checked={data.bpjs.jht} onChange={fb("jht")} testid="pph21-bpjs-jht-switch" disabled={ro} />
                <SwitchRow label="JP" checked={data.bpjs.jp} onChange={fb("jp")} testid="pph21-bpjs-jp-switch" disabled={ro} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Batas upah BPJS Kes">
                  <MoneyInput value={data.bpjs.kesCap} onChange={fb("kesCap")} testid="pph21-kes-cap-input" disabled={ro} />
                </Field>
                <Field label="Batas upah JP">
                  <MoneyInput value={data.bpjs.jpCap} onChange={fb("jpCap")} testid="pph21-jp-cap-input" disabled={ro} />
                </Field>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <div className="space-y-5 min-w-0">
        <div className="grid sm:grid-cols-2 2xl:grid-cols-4 gap-3">
          <KpiCard label="PPh 21 bulan reguler" value={fmtRp(r.regular.pph)} sub={`TER kategori ${r.category} · tarif ${fmtPct(r.regular.terRate)}`} accent icon={Percent} testid="pph21-kpi-monthly-tax" />
          <KpiCard label="Take home pay" value={fmtRp(r.regular.thp)} sub="Bulan reguler (tanpa bonus/THR)" icon={Wallet} testid="pph21-kpi-takehome" />
          <KpiCard label="Biaya perusahaan" value={fmtRp(r.regular.companyCost)} sub="Per bulan termasuk BPJS perusahaan" icon={Building2} testid="pph21-kpi-company-cost" />
          <KpiCard
            label="PPh 21 Desember"
            value={fmtRp(r.pphDec)}
            sub={r.pphDec < 0 ? "Lebih potong — dikembalikan ke karyawan" : "Rekonsiliasi tarif Pasal 17"}
            tone={r.pphDec < 0 ? "good" : undefined}
            icon={CalendarCheck}
            testid="pph21-kpi-december"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <SectionCard title="Rincian bulan reguler" description={`Bulan ${MONTHS_ID[r.regular.month - 1]}`} testid="pph21-monthly-breakdown">
            <Row label="Gaji pokok" value={fmtRp(r.regular.gp)} />
            <Row label="Tunjangan tetap + lainnya" value={fmtRp(r.regular.tt + r.regular.tl)} />
            <Row label="JKK + JKM (perusahaan)" value={fmtRp(r.regular.jkk + r.regular.jkm)} muted />
            <Row label="BPJS Kesehatan 4% (perusahaan)" value={fmtRp(r.regular.kesEr)} muted />
            {r.regular.tunjanganPajak > 0 && <Row label="Tunjangan PPh (gross-up)" value={fmtRp(r.regular.tunjanganPajak)} />}
            <Row label="Penghasilan bruto" value={fmtRp(r.regular.bruto)} strong />
            <Row label={`PPh 21 (TER ${r.category} ${fmtPct(r.regular.terRate)}${r.mult > 1 ? " × 120%" : ""})`} value={fmtRp(r.regular.pph)} negative />
            <Row label="Potongan JHT 2% + JP 1% + Kes 1%" value={fmtRp(r.regular.potonganBpjs)} negative />
            <Row label="Take home pay" value={fmtRp(r.regular.thp)} strong />
          </SectionCard>
          <SectionCard title="Perhitungan setahun (Pasal 17)" description="Dipakai pada masa pajak terakhir (Desember)" testid="pph21-annual-breakdown">
            <Row label="Bruto setahun" value={fmtRp(r.annual.totalBruto)} />
            <Row label="Biaya jabatan (5%, maks 6 jt)" value={fmtRp(-r.annual.biayaJabatan)} negative />
            <Row label="Iuran JHT + JP karyawan" value={fmtRp(-r.annual.iuran)} negative />
            <Row label="Penghasilan neto" value={fmtRp(r.annual.neto)} />
            <Row label={`PTKP (${data.status})`} value={fmtRp(-r.ptkp)} negative />
            <Row label="PKP (dibulatkan ribuan)" value={fmtRp(r.annual.pkp)} />
            {r.annual.layers.map((l, i) => (
              <Row key={i} label={`Lapis ${fmtPct(l.rate, 0)} × ${fmtRp(l.portion)}`} value={fmtRp(l.tax)} muted />
            ))}
            <Row label="PPh 21 setahun" value={fmtRp(r.annual.tax)} strong testid="pph21-annual-tax" />
            <Row label="Dipotong Jan–Nov (TER)" value={fmtRp(r.sum11)} muted />
            <Row label="PPh 21 Desember" value={fmtRp(r.pphDec)} strong />
          </SectionCard>
        </div>

        <SectionCard
          title="Jadwal pemotongan Januari – Desember"
          description={`Tarif efektif setahun ${fmtPct(r.effectiveRate)} · Take home setahun ${fmtRp(r.thpAnnual)} · Biaya perusahaan setahun ${fmtRp(r.costAnnual)}`}
          actions={
            <Button variant="secondary" size="sm" onClick={exportCsv} className="border border-border" data-testid="pph21-export-csv-button">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          }
          bodyClassName="p-0"
          testid="pph21-schedule-card"
        >
          <div className="overflow-x-auto scroll-gold">
            <Table className="dense-table" data-testid="pph21-schedule-table">
              <TableHeader>
                <TableRow className="bg-secondary hover:bg-secondary">
                  <TableHead>Bulan</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Bonus/THR</TableHead>
                  <TableHead className="text-right">Tunj. PPh</TableHead>
                  <TableHead className="text-right">Tarif</TableHead>
                  <TableHead className="text-right">PPh 21</TableHead>
                  <TableHead className="text-right">Take home</TableHead>
                  <TableHead className="text-right">Biaya perusahaan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.months.map((m) => (
                  <TableRow key={m.month} className={m.reconciliation ? "bg-[hsl(43_30%_7%)]" : ""}>
                    <TableCell className="font-medium">{MONTHS_ID[m.month - 1]}</TableCell>
                    <TableCell className="text-right num">{fmtRp(m.bruto)}</TableCell>
                    <TableCell className="text-right num text-muted-foreground">{m.irregular ? fmtRp(m.irregular) : "–"}</TableCell>
                    <TableCell className="text-right num text-muted-foreground">{m.tunjanganPajak ? fmtRp(m.tunjanganPajak) : "–"}</TableCell>
                    <TableCell className="text-right num">{m.reconciliation ? <span className="text-primary">Pasal 17</span> : fmtPct(m.terRate)}</TableCell>
                    <TableCell className={`text-right num ${m.pph < 0 ? "text-[hsl(160_55%_52%)]" : ""}`}>{fmtRp(m.pph)}</TableCell>
                    <TableCell className="text-right num">{fmtRp(m.thp)}</TableCell>
                    <TableCell className="text-right num text-muted-foreground">{fmtRp(m.companyCost)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-secondary">
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className="text-right num">{fmtRp(r.annual.totalBruto)}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right num text-primary font-semibold" data-testid="pph21-schedule-total-tax">{fmtRp(r.totalPph)}</TableCell>
                  <TableCell className="text-right num">{fmtRp(r.thpAnnual)}</TableCell>
                  <TableCell className="text-right num">{fmtRp(r.costAnnual)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function TidakTetap({ data, set, r, ro }) {
  const f = (k) => (v) => set((d) => ({ ...d, [k]: v }));
  return (
    <div className="grid xl:grid-cols-[420px_1fr] gap-5">
      <SectionCard title="Input Upah" eyebrow="Pegawai Tidak Tetap" testid="pph21tt-input-card">
        <div className="space-y-4">
          <Field label="Cara pembayaran">
            <Segmented value={data.payMode} onChange={f("payMode")} testid="pph21tt-paymode" disabled={ro} size="sm" options={[{ value: "harian", label: "Harian / mingguan / borongan" }, { value: "bulanan", label: "Bulanan" }]} />
          </Field>
          <Field label="Metode">
            <Segmented value={data.method} onChange={f("method")} testid="pph21tt-method" disabled={ro} size="sm" options={[{ value: "gross", label: "Gross (dipotong)" }, { value: "grossup", label: "Gross-up" }]} />
          </Field>
          {data.payMode === "harian" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Upah per hari">
                <MoneyInput value={data.upahHarian} onChange={f("upahHarian")} testid="pph21tt-upah-harian-input" disabled={ro} />
              </Field>
              <Field label="Jumlah hari kerja">
                <NumInput value={data.hariKerja} onChange={f("hariKerja")} decimals={0} suffix="hari" testid="pph21tt-hari-input" disabled={ro} />
              </Field>
            </div>
          ) : (
            <>
              <Field label="Upah per bulan">
                <MoneyInput value={data.upahBulanan} onChange={f("upahBulanan")} testid="pph21tt-upah-bulanan-input" disabled={ro} />
              </Field>
              <Field label="Status PTKP">
                <Sel value={data.status} onChange={f("status")} options={statusOptions} testid="pph21tt-status-select" disabled={ro} />
              </Field>
            </>
          )}
          <SwitchRow label="Memiliki NPWP / NIK valid" hint="Tanpa NPWP tarif 20% lebih tinggi." checked={data.npwp} onChange={f("npwp")} testid="pph21tt-npwp-switch" disabled={ro} />
        </div>
      </SectionCard>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <KpiCard label="PPh 21 dipotong" value={fmtRp(r.pph)} sub={r.scheme} accent testid="pph21tt-kpi-tax" />
          <KpiCard label="Total bruto" value={fmtRp(r.totalBruto)} sub={r.dailyBruto ? `${fmtRp(r.dailyBruto)} / hari` : "Per bulan"} testid="pph21tt-kpi-bruto" />
          <KpiCard label="Diterima bersih" value={fmtRp(r.net)} sub={`Tarif efektif ${fmtPct(r.rate)}`} testid="pph21tt-kpi-net" />
        </div>
        <SectionCard title="Rincian">
          <Row label="Skema" value={r.scheme} />
          {r.tunjanganPajak > 0 && <Row label="Tunjangan PPh (gross-up)" value={fmtRp(r.tunjanganPajak)} />}
          <Row label="Penghasilan bruto" value={fmtRp(r.totalBruto)} />
          <Row label="Tarif efektif" value={fmtPct(r.rate)} />
          <Row label="PPh 21" value={fmtRp(r.pph)} negative />
          <Row label="Diterima pegawai" value={fmtRp(r.net)} strong />
          <InfoNote className="mt-4">{r.note}</InfoNote>
        </SectionCard>
      </div>
    </div>
  );
}

function BukanPegawai({ data, set, r, ro }) {
  const f = (k) => (v) => set((d) => ({ ...d, [k]: v }));
  return (
    <div className="grid xl:grid-cols-[420px_1fr] gap-5">
      <SectionCard title="Input Imbalan" eyebrow="Bukan Pegawai" description="Tenaga ahli, konsultan, freelancer, narasumber, agen, dll." testid="pph21bp-input-card">
        <div className="space-y-4">
          <Field label="Jenis penerima">
            <TextInput value={data.jenis} onChange={f("jenis")} testid="pph21bp-jenis-input" disabled={ro} />
          </Field>
          <Field label="Nilai yang diinput">
            <Segmented value={data.inputBasis} onChange={f("inputBasis")} testid="pph21bp-basis" disabled={ro} size="sm" options={[{ value: "bruto", label: "Bruto (sebelum pajak)" }, { value: "net", label: "Net diterima (gross-up)" }]} />
          </Field>
          <Field label={data.inputBasis === "net" ? "Nilai bersih yang ingin diterima" : "Imbalan bruto"}>
            <MoneyInput value={data.amount} onChange={f("amount")} testid="pph21bp-amount-input" disabled={ro} />
          </Field>
          <Field label="Kumulatif bruto sebelumnya (tahun berjalan)" hint="Isi bila pembayaran berkesinambungan ke orang yang sama, agar lapisan tarif Pasal 17 dihitung kumulatif.">
            <MoneyInput value={data.kumulatif} onChange={f("kumulatif")} testid="pph21bp-kumulatif-input" disabled={ro} />
          </Field>
          <SwitchRow label="Memiliki NPWP / NIK valid" hint="Tanpa NPWP tarif 20% lebih tinggi." checked={data.npwp} onChange={f("npwp")} testid="pph21bp-npwp-switch" disabled={ro} />
        </div>
      </SectionCard>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <KpiCard label="PPh 21 dipotong" value={fmtRp(r.pph)} sub={`Tarif efektif ${fmtPct(r.effectiveRate)}`} accent testid="pph21bp-kpi-tax" />
          <KpiCard label="Imbalan bruto" value={fmtRp(r.bruto)} sub={`DPP 50% = ${fmtRp(r.dpp)}`} testid="pph21bp-kpi-bruto" />
          <KpiCard label="Diterima bersih" value={fmtRp(r.net)} testid="pph21bp-kpi-net" />
        </div>
        <SectionCard title="Rincian tarif Pasal 17 atas 50% bruto">
          <Row label="Imbalan bruto" value={fmtRp(r.bruto)} />
          <Row label="DPP (50% × bruto)" value={fmtRp(r.dpp)} />
          {r.prevDpp > 0 && <Row label="DPP kumulatif sebelumnya" value={fmtRp(r.prevDpp)} muted />}
          {r.layers.map((l, i) => (
            <Row key={i} label={`Lapis ${fmtPct(l.rate, 0)} (${fmtRp(l.from)} – ${fmtRp(l.to)})`} value={fmtRp(l.tax)} muted />
          ))}
          <Row label="PPh 21 periode ini" value={fmtRp(r.pph)} strong />
          <InfoNote className="mt-4">PMK 168/2023: PPh 21 bukan pegawai = tarif Pasal 17 ayat (1) huruf a × 50% jumlah penghasilan bruto.</InfoNote>
        </SectionCard>
      </div>
    </div>
  );
}

function ReferenceCard() {
  return (
    <SectionCard title="Catatan & referensi" eyebrow="PPh 21 vs PPh 23" testid="tax-reference-card">
      <div className="grid md:grid-cols-3 gap-4 text-xs text-[hsl(45_15%_80%)] leading-relaxed">
        <div className="flex gap-3">
          <Landmark className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p>
            <b className="text-foreground">PPh 21</b> dikenakan atas penghasilan <b>orang pribadi</b> (pegawai, bukan pegawai) dari pekerjaan/jasa. Sejak 2024 memakai TER bulanan (PP 58/2023) dan Desember dihitung ulang dengan tarif Pasal 17.
          </p>
        </div>
        <div className="flex gap-3">
          <Building2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p>
            <b className="text-foreground">PPh 23</b> dipotong atas pembayaran ke <b>badan usaha</b> untuk jasa/sewa (2%) atau dividen, bunga, royalti, hadiah (15%). Tanpa NPWP tarif 100% lebih tinggi.
          </p>
        </div>
        <div className="flex gap-3">
          <Percent className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p>
            Referensi: kalkulator.pajak.go.id, online-pajak.com, PP 58/2023, PMK 168/2023, UU HPP, PMK 101/2016. Hasil kalkulator bersifat simulasi — konfirmasi ke konsultan pajak untuk pelaporan resmi.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}
